import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, Pencil, Trash2, LayoutTemplate, X, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

//const DURATIONS = [15, 30, 45, 60, 90];
const DURATIONS = [20];
const hiddenNames = ["Board Meeting", "Team All-Hands","Sales Demo"];
const QTYPES = [
  { v: "text", l: "Short text" }, { v: "textarea", l: "Paragraph" },
  { v: "dropdown", l: "Dropdown" }, { v: "checkbox", l: "Yes / No" },
];

const testidFor = (name) => `template-card-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

const emptyTemplate = () => ({
  name: "", description: "", duration_minutes: 30, color: "#2563EB",
  questions: [], reminder_email_24h: true, reminder_email_1h: false, active: true,
});

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTemplate());
  const [saving, setSaving] = useState(false);

  const load = async () => setTemplates((await api.get("/templates")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyTemplate()); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, description: t.description, duration_minutes: t.duration_minutes, color: t.color,
      questions: t.questions || [], reminder_email_24h: t.reminder_email_24h, reminder_email_1h: t.reminder_email_1h, active: t.active });
    setOpen(true);
  };

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  const addQ = () => setF({ questions: [...form.questions, { id: Math.random().toString(36).slice(2, 10), label: "", type: "text", required: true, options: [] }] });
  const setQ = (i, patch) => setF({ questions: form.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) });
  const rmQ = (i) => setF({ questions: form.questions.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give your template a name"); return; }
    setSaving(true);
    try {
      const payload = { ...form, questions: form.questions.map((q) => ({ ...q, options: q.type === "dropdown" ? (q.options || []) : [] })) };
      if (editing) await api.put(`/templates/${editing.template_id}`, payload);
      else await api.post("/templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    await api.delete(`/templates/${t.template_id}`);
    toast.success("Template deleted");
    load();
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
    toast.success("Booking link copied");
  };

  return (
    <div className="space-y-8" data-testid="templates-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Meeting templates</h1>
          <p className="mt-1 text-slate-600">One scenario, one link, one set of rules.</p>
        </div>
        <button onClick={openCreate} className="brand-btn" data-testid="btn-create-template">
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.filter((t) => !hiddenNames.includes(t.name)).map((t) => (
          <div key={t.template_id} data-testid={testidFor(t.name)}
            className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.color }}>
                  <LayoutTemplate className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight">{t.name}</h3>
                  <p className="text-xs text-slate-500">{t.duration_minutes} min · {t.questions?.length || 0} questions</p>
                </div>
              </div>
              {!t.active && <span className="text-xs font-medium text-slate-400">Off</span>}
            </div>
            <p className="mt-3 text-sm text-slate-600 line-clamp-2 flex-1">{t.description || "No description"}</p>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={() => copyLink(t.slug)} data-testid={`copy-${t.slug}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
                <Copy className="w-4 h-4" /> Copy link
              </button>
              <button onClick={() => openEdit(t)} data-testid={`edit-${t.slug}`} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Pencil className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => del(t)} data-testid={`delete-${t.slug}`} className="p-2 rounded-lg border border-slate-200 hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Name</label>
              <input value={form.name} data-testid="template-name-input" onChange={(e) => setF({ name: e.target.value })}
                placeholder="e.g. Sales Demo" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
              <textarea value={form.description} data-testid="template-desc-input" onChange={(e) => setF({ description: e.target.value })}
                rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Duration</label>
                <select value={form.duration_minutes} data-testid="template-duration-select" onChange={(e) => setF({ duration_minutes: parseInt(e.target.value, 10) })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                  {DURATIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Accent color</label>
                <input type="color" value={form.color} onChange={(e) => setF({ color: e.target.value })}
                  className="w-full h-[38px] rounded-lg border border-slate-300 px-1 py-1 cursor-pointer" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Pre-meeting questions</label>
                <button onClick={addQ} data-testid="add-question-btn" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"><Plus className="w-4 h-4" /> Add question</button>
              </div>
              <div className="space-y-3">
                {form.questions.map((q, i) => (
                  <div key={q.id} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid={`question-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-slate-300" />
                      <input value={q.label} onChange={(e) => setQ(i, { label: e.target.value })} placeholder="Question label"
                        data-testid={`question-label-${i}`} className="flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                      <button onClick={() => rmQ(i)} className="p-1 text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-3 mt-2 pl-6 flex-wrap">
                      <select value={q.type} onChange={(e) => setQ(i, { type: e.target.value })} data-testid={`question-type-${i}`}
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none">
                        {QTYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                      </select>
                      <label className="flex items-center gap-1.5 text-sm text-slate-600">
                        <input type="checkbox" checked={q.required} onChange={(e) => setQ(i, { required: e.target.checked })} /> Required
                      </label>
                      {q.type === "dropdown" && (
                        <input value={(q.options || []).join(", ")} onChange={(e) => setQ(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                          placeholder="Option 1, Option 2" className="flex-1 min-w-[160px] rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Email reminder 24h before</span>
                <Switch checked={form.reminder_email_24h} onCheckedChange={(v) => setF({ reminder_email_24h: v })} data-testid="reminder-24h-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Email reminder 1h before</span>
                <Switch checked={form.reminder_email_1h} onCheckedChange={(v) => setF({ reminder_email_1h: v })} data-testid="reminder-1h-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Active (accepting bookings)</span>
                <Switch checked={form.active} onCheckedChange={(v) => setF({ active: v })} data-testid="template-active-toggle" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} className="brand-btn" data-testid="save-template-btn">{saving ? "Saving…" : editing ? "Save" : "Create"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
